import type {
  ActivePipelinesResponse,
  AiHistoryResponse,
  AiModelPricingEntry,
  AiPricingResponse,
  AiUsageStats,
  AuditLogResponse,
  AuditLogStatsResponse,
  BranchColorRules,
  BranchJiraResolution,
  CiConfig,
  CiConfigInput,
  ClaudeApiKeyDTO,
  ClaudeApiKeyVerifyResponse,
  CommitCiDTO,
  PackageCommitsResponse,
  CredentialsInput,
  CreateCherryPickInput,
  GraphDTO,
  JiraAccessDTO,
  JiraAssignableUserDTO,
  JiraVersionDTO,
  JiraBaseUrlVerifyResponse,
  JiraIssueSuggestionDTO,
  JiraTicketDTO,
  JiraTokenDTO,
  JiraTokenVerifyResponse,
  JiraTransitionDTO,
  ManualBranchLinkInput,
  MergeMergeRequestResponse,
  MergeRequestDetailDTO,
  MergeRequestsResponse,
  PipelineStatusResponse,
  PlayJobResponse,
  RepoTicketsResponse,
  TraceabilityConfigInput,
  TraceabilityDTO,
  SessionDTO,
  SummarizedShasResponse,
  SummaryResponse,
  SummarizeCommitInput,
  SummarizeRangeInput,
  UpdateAssigneeInput,
  UpdateFixVersionsInput,
  UserPreferences,
} from '@clearflow/shared';

export type GitlabVisibility = 'public' | 'internal' | 'private';
export type GitlabRole = 'guest' | 'reporter' | 'developer' | 'maintainer' | 'owner';

export interface AvailableProjectDTO {
  id: number;
  name: string;
  pathWithNamespace: string;
  defaultBranch: string;
  webUrl: string;
  lastActivityAt: string;
  visibility: GitlabVisibility;
  role: GitlabRole | null;
}

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
export const API_BASE_URL = BASE;

// Même message qu'avant (`HTTP <statut>: <corps>`), mais le statut et le corps restent lisibles : un
// appelant peut en tirer l'erreur structurée d'une route (ex. PlayJobError).
export class HttpError extends Error {
  constructor(public status: number, public body: string) {
    super(`HTTP ${status}: ${body}`);
  }
}

async function http<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      // Fastify rejette un body JSON vide quand ce header est posé sans body (FST_ERR_CTP_EMPTY_JSON_BODY) —
      // ne l'ajouter que si on envoie effectivement un body.
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new HttpError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Consomme une réponse NDJSON ligne par ligne : chaque ligne est un snapshot complet
// (pas un delta), donc `onChunk` peut juste remplacer l'état précédent. Résout avec la
// dernière ligne reçue une fois le flux terminé.
async function streamNdjson<T>(path: string, onChunk: (value: T) => void): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body}`);
  }
  if (!res.body) throw new Error('Réponse sans corps de flux');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let last: T | undefined;

  const consumeLine = (line: string) => {
    if (!line) return;
    last = JSON.parse(line) as T;
    onChunk(last);
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
      consumeLine(buffer.slice(0, newlineIndex));
      buffer = buffer.slice(newlineIndex + 1);
    }
  }
  consumeLine(buffer);

  if (last === undefined) throw new Error('Flux vide');
  return last;
}

export const api = {
  // Auth
  saveCredentials: (body: CredentialsInput) =>
    http<SessionDTO>('/auth/credentials', { method: 'POST', body: JSON.stringify(body) }),
  saveJiraCredentials: (jiraToken: string, jiraBaseUrl?: string) =>
    http<SessionDTO>('/auth/jira-credentials', { method: 'POST', body: JSON.stringify({ jiraToken, jiraBaseUrl }) }),
  verifyJiraToken: (jiraToken: string, jiraBaseUrl?: string) =>
    http<JiraTokenVerifyResponse>('/auth/jira-credentials/verify', {
      method: 'POST',
      body: JSON.stringify({ jiraToken, jiraBaseUrl }),
    }),
  deleteJiraCredentials: () => http<SessionDTO>('/auth/jira-credentials', { method: 'DELETE' }),
  verifyJiraBaseUrl: (jiraBaseUrl: string) =>
    http<JiraBaseUrlVerifyResponse>('/auth/jira-base-url/verify', {
      method: 'POST',
      body: JSON.stringify({ jiraBaseUrl }),
    }),
  getSession: () => http<SessionDTO>('/auth/session'),
  getJiraToken: () => http<JiraTokenDTO>('/auth/jira-token'),
  saveClaudeApiKey: (apiKey: string) =>
    http<SessionDTO>('/auth/claude-api-key', { method: 'POST', body: JSON.stringify({ apiKey }) }),
  verifyClaudeApiKey: (apiKey: string) =>
    http<ClaudeApiKeyVerifyResponse>('/auth/claude-api-key/verify', {
      method: 'POST',
      body: JSON.stringify({ apiKey }),
    }),
  deleteClaudeApiKey: () => http<SessionDTO>('/auth/claude-api-key', { method: 'DELETE' }),
  getClaudeApiKey: () => http<ClaudeApiKeyDTO>('/auth/claude-api-key'),
  logout: () => http<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  // Préférences UI (thème, sections de sidebar visibles…) — persistées par compte, voir
  // apps/api/src/routes/preferences.ts. patch() est un merge partiel côté serveur : envoyer un
  // seul champ ne touche pas les autres déjà enregistrés.
  getPreferences: () => http<UserPreferences>('/users/me/preferences'),
  updatePreferences: (patch: UserPreferences) =>
    http<UserPreferences>('/users/me/preferences', { method: 'PATCH', body: JSON.stringify(patch) }),

  // Repos
  // Le scan GitLab complet prend ~20s pour un compte avec beaucoup de projets — l'API
  // streame les résultats page par page en NDJSON ; chaque `onPage` reçoit la liste
  // triée-à-date au complet pour affichage progressif au lieu d'attendre le scan entier.
  streamAvailable: (onPage: (projects: AvailableProjectDTO[]) => void) =>
    streamNdjson<AvailableProjectDTO[]>('/repos/available', onPage),
  listRepos: () =>
    http<
      Array<{
        id: string;
        name: string;
        pathWithNamespace: string;
        gitlabProjectId: number;
        visibility: GitlabVisibility | null;
        role: GitlabRole | null;
      }>
    >('/repos'),
  importRepo: (body: {
    gitlabProjectId: number;
    pathWithNamespace: string;
    name: string;
    defaultBranch: string;
    webUrl: string;
  }) =>
    http<{ id: string }>('/repos/import', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getRepo: (id: string) =>
    http<{
      id: string;
      name: string;
      pathWithNamespace: string;
      visibility: GitlabVisibility | null;
      role: GitlabRole | null;
      branchColorRules: BranchColorRules;
      srsPageId: string | null;
      sddPageId: string | null;
      // null = aucune config enregistrée : liste par défaut (DEFAULT_CI_ALLOWED_STAGES).
      ciConfig: CiConfig | null;
    }>(`/repos/${id}`),
  traceability: (repoId: string) => http<TraceabilityDTO>(`/repos/${repoId}/traceability`),
  updateTraceabilityConfig: (repoId: string, input: { srsPageId: string | null; sddPageId: string | null }) =>
    http<TraceabilityConfigInput>(`/repos/${repoId}/traceability-config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  updateCiConfig: (repoId: string, input: CiConfigInput) =>
    http<CiConfigInput>(`/repos/${repoId}/ci-config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  updateBranchColorRules: (repoId: string, rules: BranchColorRules) =>
    http<BranchColorRules>(`/repos/${repoId}/branch-color-rules`, {
      method: 'PUT',
      body: JSON.stringify(rules),
    }),
  removeRepo: (repoId: string) => http<void>(`/repos/${repoId}`, { method: 'DELETE' }),
  graph: (id: string) => http<GraphDTO>(`/repos/${id}/graph`),
  packageCommits: (repoId: string) => http<PackageCommitsResponse>(`/repos/${repoId}/package-commits`),
  commitCi: (repoId: string, sha: string) => http<CommitCiDTO>(`/repos/${repoId}/commits/${sha}/ci`),
  playJob: (repoId: string, jobId: number) =>
    http<PlayJobResponse>(`/repos/${repoId}/jobs/${jobId}/play`, { method: 'POST' }),
  summarizedShas: (id: string) => http<SummarizedShasResponse>(`/repos/${id}/summarized-shas`),
  pipelineStatus: (repoId: string, branchName: string) =>
    http<PipelineStatusResponse>(
      `/repos/${repoId}/pipeline-status?branchName=${encodeURIComponent(branchName)}`,
    ),
  activePipelines: (repoId: string) =>
    http<ActivePipelinesResponse>(`/repos/${repoId}/active-pipelines`),
  mergeRequests: (repoId: string) =>
    http<MergeRequestsResponse>(`/repos/${repoId}/merge-requests`),
  mergeRequestDetail: (repoId: string, iid: number) =>
    http<MergeRequestDetailDTO>(`/repos/${repoId}/merge-requests/${iid}`),
  mergeMergeRequest: (repoId: string, iid: number, sha: string) =>
    http<MergeMergeRequestResponse>(`/repos/${repoId}/merge-requests/${iid}/merge`, {
      method: 'POST',
      body: JSON.stringify({ sha }),
    }),
  deleteTag: (repoId: string, tagName: string) =>
    http<void>(`/repos/${repoId}/tags?name=${encodeURIComponent(tagName)}`, { method: 'DELETE' }),
  createTag: (repoId: string, name: string, ref: string, message?: string) =>
    http<{ name: string }>(`/repos/${repoId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ name, ref, message }),
    }),
  createBranch: (repoId: string, name: string, ref: string) =>
    http<{ name: string }>(`/repos/${repoId}/branches`, {
      method: 'POST',
      body: JSON.stringify({ name, ref }),
    }),
  deleteBranch: (repoId: string, branchName: string) =>
    http<void>(`/repos/${repoId}/branches?name=${encodeURIComponent(branchName)}`, { method: 'DELETE' }),

  // Cherry-picks
  listCherryPicks: (repoId: string) =>
    http<Array<{ id: string; sourceSha: string; targetSha: string; note: string | null }>>(
      `/cherry-picks?repoId=${encodeURIComponent(repoId)}`,
    ),
  createCherryPick: (body: CreateCherryPickInput) =>
    http<{ id: string }>('/cherry-picks', { method: 'POST', body: JSON.stringify(body) }),
  // Renvoie le CherryPickLink Prisma brut (routes/cherryPicks.ts), pas un CherryPickEdgeDTO —
  // celui-ci n'existe qu'une fois enrichi (createdByName) par le service de graphe ; seul `note`
  // nous intéresse ici, donc pas la peine de sur-typer le reste.
  updateCherryPickNote: (id: string, note: string | null) =>
    http<{ note: string | null }>(`/cherry-picks/${id}`, { method: 'PATCH', body: JSON.stringify({ note }) }),
  deleteCherryPick: (id: string) => http<void>(`/cherry-picks/${id}`, { method: 'DELETE' }),

  // AI
  summarizeCommit: (body: SummarizeCommitInput) =>
    http<SummaryResponse>('/ai/summarize/commit', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  summarizeRange: (body: SummarizeRangeInput) =>
    http<SummaryResponse>('/ai/summarize/range', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  aiUsage: () => http<AiUsageStats>('/ai/usage'),
  aiHistory: (page = 1) => http<AiHistoryResponse>(`/ai/history?page=${page}`),
  auditLog: (page = 1) => http<AuditLogResponse>(`/audit-log?page=${page}`),
  auditLogStats: () => http<AuditLogStatsResponse>('/audit-log/stats'),
  aiPricing: () => http<AiPricingResponse>('/ai/pricing'),
  updateAiPricing: (modelId: string, body: { inputPerToken: number; outputPerToken: number }) =>
    http<AiModelPricingEntry>(`/ai/pricing/${encodeURIComponent(modelId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // Jira
  jiraAccess: () => http<JiraAccessDTO>('/jira/access'),
  resolveBranchTicket: (repoId: string, branchName: string) =>
    http<BranchJiraResolution>(
      `/jira/branch?repoId=${encodeURIComponent(repoId)}&branchName=${encodeURIComponent(branchName)}`,
    ),
  repoTickets: (repoId: string, branchNames: string[]) =>
    http<RepoTicketsResponse>('/jira/repo-tickets', {
      method: 'POST',
      body: JSON.stringify({ repoId, branchNames }),
    }),
  ticketDetail: (ticketKey: string) => http<JiraTicketDTO>(`/jira/tickets/${encodeURIComponent(ticketKey)}`),
  linkBranch: (body: ManualBranchLinkInput) =>
    http<{ id: string }>('/jira/branch-link', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  unlinkBranch: (repoId: string, branchName: string) =>
    http<{ ok: boolean }>(
      `/jira/branch-link?repoId=${encodeURIComponent(repoId)}&branchName=${encodeURIComponent(branchName)}`,
      { method: 'DELETE' },
    ),
  updateAssignee: (body: UpdateAssigneeInput) =>
    http<{ ok: boolean }>(`/jira/tickets/${body.ticketKey}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ accountId: body.accountId }),
    }),
  assignableUsers: (ticketKey: string, query: string) =>
    http<JiraAssignableUserDTO[]>(
      `/jira/tickets/${encodeURIComponent(ticketKey)}/assignable-users?query=${encodeURIComponent(query)}`,
    ),
  ticketTransitions: (ticketKey: string) =>
    http<JiraTransitionDTO[]>(`/jira/tickets/${encodeURIComponent(ticketKey)}/transitions`),
  unreleasedVersions: (ticketKey: string) =>
    http<JiraVersionDTO[]>(`/jira/tickets/${encodeURIComponent(ticketKey)}/unreleased-versions`),
  updateFixVersions: (body: UpdateFixVersionsInput) =>
    http<{ ok: boolean }>(`/jira/tickets/${encodeURIComponent(body.ticketKey)}/fix-versions`, {
      method: 'PUT',
      body: JSON.stringify({ versionIds: body.versionIds }),
    }),
  issuePicker: (query: string) =>
    http<JiraIssueSuggestionDTO[]>(`/jira/issue-picker?query=${encodeURIComponent(query)}`),
  transitionTicket: (ticketKey: string, transitionId: string) =>
    http<{ ok: boolean }>(`/jira/tickets/${encodeURIComponent(ticketKey)}/transition`, {
      method: 'POST',
      body: JSON.stringify({ transitionId }),
    }),
};

export type Project = AvailableProjectDTO;
export type RepoRow = Awaited<ReturnType<typeof api.listRepos>>[number];
export type { JiraTicketDTO };
